import dayjs from "dayjs";
import { OnboardingLiveCancelledBy } from "../../gql/graphql.ts";
import { onboardingLiveTimezone } from "../onboardingLiveTime.ts";
import {
  StatisticsPeriod,
  weekStartDayOf,
} from "./onboardingLiveStatisticsPeriod.ts";

export type StatisticsLive = {
  id: string;
  startUtc: string;
  teacherId: string;
  participantsCount: number;
  hasEnded: boolean;
  feedback?: { participantsCount: number } | null;
};

export type StatisticsDeletedLive = { participantsCount: number };

export type StatisticsCancellation = { cancelledBy: OnboardingLiveCancelledBy };

export type StatisticsConductorChange = { fromTeacherId: string };

export type Peak = { value: number; startUtc: string };

export type WeeklyOnboardingLives = {
  weekStartDay: string;
  lives: number;
  enrolled: number;
  attended: number;
};

export type WeekdayHourEnrollment = {
  weekdayFromMonday: number;
  hour: number;
  lives: number;
  averageEnrolled: number;
};

export type ConductorStatistics = {
  teacherId: string;
  lives: number;
  averageEnrolled: number | null;
  averageAttended: number | null;
  feedbackShare: number | null;
  timesReplaced: number;
};

export type OnboardingLiveStatistics = {
  doneLives: number;
  livesPerDay: number;
  daysWithALive: number;
  daysInPeriod: number;
  averageEnrolled: number | null;
  enrolledPeak?: Peak;
  averageAttended: number | null;
  attendedPeak?: Peak;
  livesWithFeedback: number;
  attendanceRate: number | null;
  feedbackShare: number | null;
  weeks: WeeklyOnboardingLives[];
  enrollmentByWeekdayAndHour: WeekdayHourEnrollment[];
  conductors: ConductorStatistics[];
  cancellationsByStudents: number;
  cancellationsByStaff: number;
  deletedLives: number;
  studentsOfDeletedLives: number;
  conductorChanges: number;
};

function shareOf(part: number, total: number): number | null {
  return total > 0 ? part / total : null;
}

function sumOf<T>(items: T[], valueOf: (item: T) => number): number {
  return items.reduce((sum, item) => sum + valueOf(item), 0);
}

function averageOf<T>(items: T[], valueOf: (item: T) => number): number | null {
  return items.length > 0 ? sumOf(items, valueOf) / items.length : null;
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return groups;
}

function peakOf(
  lives: StatisticsLive[],
  valueOf: (live: StatisticsLive) => number,
): Peak | undefined {
  return lives.reduce<Peak | undefined>(
    (peak, live) =>
      !peak || valueOf(live) > peak.value
        ? { value: valueOf(live), startUtc: live.startUtc }
        : peak,
    undefined,
  );
}

function enrolledOf(live: StatisticsLive): number {
  return live.participantsCount;
}

function attendeesOf(live: StatisticsLive): number {
  return live.feedback?.participantsCount ?? 0;
}

function hasFeedback(live: StatisticsLive): boolean {
  return !!live.feedback;
}

function startInRomeOf(live: StatisticsLive) {
  return dayjs(live.startUtc).tz(onboardingLiveTimezone);
}

function weeklyLivesOf(
  doneLives: StatisticsLive[],
  period: StatisticsPeriod,
): WeeklyOnboardingLives[] {
  const livesByWeek = groupBy(doneLives, (live) =>
    weekStartDayOf(live.startUtc),
  );
  return period.weekStartDays.map((weekStartDay) => {
    const lives = livesByWeek.get(weekStartDay) ?? [];
    return {
      weekStartDay,
      lives: lives.length,
      enrolled: sumOf(lives, enrolledOf),
      attended: sumOf(lives, attendeesOf),
    };
  });
}

function enrollmentByWeekdayAndHourOf(
  doneLives: StatisticsLive[],
): WeekdayHourEnrollment[] {
  const livesBySlot = groupBy(doneLives, (live) => {
    const start = startInRomeOf(live);
    return `${(start.day() + 6) % 7}-${start.hour()}`;
  });
  return Array.from(livesBySlot, ([slot, lives]) => {
    const [weekdayFromMonday, hour] = slot.split("-").map(Number);
    return {
      weekdayFromMonday,
      hour,
      lives: lives.length,
      averageEnrolled: averageOf(lives, enrolledOf) ?? 0,
    };
  });
}

function conductorsOf(
  doneLives: StatisticsLive[],
  conductorChanges: StatisticsConductorChange[],
): ConductorStatistics[] {
  const livesByTeacher = groupBy(doneLives, (live) => live.teacherId);
  const replacementsByTeacher = groupBy(
    conductorChanges,
    (change) => change.fromTeacherId,
  );
  const teacherIds = new Set([
    ...livesByTeacher.keys(),
    ...replacementsByTeacher.keys(),
  ]);
  return Array.from(teacherIds, (teacherId) => {
    const lives = livesByTeacher.get(teacherId) ?? [];
    const livesWithFeedback = lives.filter(hasFeedback);
    return {
      teacherId,
      lives: lives.length,
      averageEnrolled: averageOf(lives, enrolledOf),
      averageAttended: averageOf(livesWithFeedback, attendeesOf),
      feedbackShare: shareOf(livesWithFeedback.length, lives.length),
      timesReplaced: replacementsByTeacher.get(teacherId)?.length ?? 0,
    };
  }).sort(
    (a, b) =>
      b.lives - a.lives || (b.averageEnrolled ?? 0) - (a.averageEnrolled ?? 0),
  );
}

export function computeOnboardingLiveStatistics({
  lives,
  deletedLives,
  cancellations,
  conductorChanges,
  period,
}: {
  lives: StatisticsLive[];
  deletedLives: StatisticsDeletedLive[];
  cancellations: StatisticsCancellation[];
  conductorChanges: StatisticsConductorChange[];
  period: StatisticsPeriod;
}): OnboardingLiveStatistics {
  const doneLives = lives.filter((live) => live.hasEnded);
  const livesWithFeedback = doneLives.filter(hasFeedback);
  const daysWithALive = new Set(
    doneLives.map((live) => startInRomeOf(live).format("YYYY-MM-DD")),
  ).size;

  return {
    doneLives: doneLives.length,
    livesPerDay: doneLives.length / period.days,
    daysWithALive,
    daysInPeriod: period.days,
    averageEnrolled: averageOf(doneLives, enrolledOf),
    enrolledPeak: peakOf(doneLives, enrolledOf),
    averageAttended: averageOf(livesWithFeedback, attendeesOf),
    attendedPeak: peakOf(livesWithFeedback, attendeesOf),
    livesWithFeedback: livesWithFeedback.length,
    attendanceRate: shareOf(
      sumOf(livesWithFeedback, attendeesOf),
      sumOf(livesWithFeedback, enrolledOf),
    ),
    feedbackShare: shareOf(livesWithFeedback.length, doneLives.length),
    weeks: weeklyLivesOf(doneLives, period),
    enrollmentByWeekdayAndHour: enrollmentByWeekdayAndHourOf(doneLives),
    conductors: conductorsOf(doneLives, conductorChanges),
    cancellationsByStudents: cancellations.filter(
      (c) => c.cancelledBy === OnboardingLiveCancelledBy.Student,
    ).length,
    cancellationsByStaff: cancellations.filter(
      (c) => c.cancelledBy === OnboardingLiveCancelledBy.Staff,
    ).length,
    deletedLives: deletedLives.length,
    studentsOfDeletedLives: sumOf(
      deletedLives,
      (live) => live.participantsCount,
    ),
    conductorChanges: conductorChanges.length,
  };
}
